import React from 'react';
import { founderDashboardAPI } from '../../services/api';
import { useFeatureAnalytics } from '../../hooks/useFeatureAnalytics';
import FilterBar from '../../components/feature-analytics/FilterBar';
import FeatureHealthCard from '../../components/feature-analytics/FeatureHealthCard';
import RefreshIndicator from '../../components/founder-dashboard/RefreshIndicator';

export default function FeatureSummaryPage() {
  const {
    data, loading, error, showError, lastUpdated, fetchData,
    organizationId, campusId, groupId, handleFilterChange
  } = useFeatureAnalytics(founderDashboardAPI.getFeatureSummary);

  if (showError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-800 mb-4">{error}</p>
        <button onClick={fetchData} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  const summary = data?.summary || {};

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Feature Health Overview</h2>
          <p className="text-slate-600 text-sm">Adoption, usage, and effectiveness across all features</p>
        </div>
        <RefreshIndicator lastUpdated={lastUpdated} onRefresh={fetchData} loading={loading} />
      </div>

      <FilterBar
        organizationId={organizationId}
        campusId={campusId}
        groupId={groupId}
        onFilterChange={handleFilterChange}
      />

      {loading && !data ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-40 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <FeatureHealthCard
            feature="prayer"
            health={summary.prayer?.health || 'yellow'}
            adoption={summary.prayer?.adoption || 0}
            metricType={summary.prayer?.metricType || 'adoption'}
            usageLabel={summary.prayer?.usageLabel || '0/user'}
            effectiveness={summary.prayer?.effectiveness || 0}
            organizationId={organizationId}
            campusId={campusId}
            groupId={groupId}
          />
          <FeatureHealthCard
            feature="attendance"
            health={summary.attendance?.health || 'yellow'}
            adoption={summary.attendance?.adoption || 0}
            metricType={summary.attendance?.metricType || 'adoption'}
            usageLabel={summary.attendance?.usageLabel || '0%'}
            effectivenessLabel={summary.attendance?.effectivenessLabel || '0 at-risk'}
            organizationId={organizationId}
            campusId={campusId}
            groupId={groupId}
          />
          <FeatureHealthCard
            feature="events"
            health={summary.events?.health || 'yellow'}
            adoption={summary.events?.adoption || 0}
            metricType={summary.events?.metricType || 'adoption'}
            usageLabel={summary.events?.usageLabel || '0/group'}
            effectiveness={summary.events?.effectiveness || 0}
            organizationId={organizationId}
            campusId={campusId}
            groupId={groupId}
          />
          <FeatureHealthCard
            feature="chat"
            health={summary.chat?.health || 'yellow'}
            adoption={summary.chat?.adoption || 0}
            metricType={summary.chat?.metricType || 'adoption'}
            usageLabel={summary.chat?.usageLabel || '0/day'}
            effectiveness={summary.chat?.effectiveness || 0}
            organizationId={organizationId}
            campusId={campusId}
            groupId={groupId}
          />
        </div>
      )}

      {/* Metrics Legend */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-800 mb-4">Metrics Guide</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-medium text-slate-700 mb-2">Health Colors</h4>
            <ul className="space-y-1 text-slate-600">
              <li className="flex items-center gap-2">
                <span>🟢</span> Green: 60%+ adoption
              </li>
              <li className="flex items-center gap-2">
                <span>🟡</span> Yellow: 30-59% adoption
              </li>
              <li className="flex items-center gap-2">
                <span>🔴</span> Red: &lt;30% adoption
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-slate-700 mb-2">Adoption</h4>
            <p className="text-slate-600">
              Percentage of groups actively using this feature in the last 30 days (7 days for chat).
            </p>
          </div>
          <div>
            <h4 className="font-medium text-slate-700 mb-2">Effectiveness</h4>
            <ul className="space-y-1 text-slate-600">
              <li><strong>Prayer:</strong> Answer rate</li>
              <li><strong>Attendance:</strong> At-risk member count</li>
              <li><strong>Events:</strong> RSVP response rate</li>
              <li><strong>Chat:</strong> Media share rate</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Timestamp */}
      {data?.timestamp && (
        <p className="text-xs text-slate-400 mt-4 text-right">
          Data as of: {new Date(data.timestamp).toLocaleString()}
        </p>
      )}
    </div>
  );
}
